import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle2, XCircle, Lightbulb, BookOpen, Clock, AlertTriangle, Trophy, PenLine, Square, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import ScratchPad from '@/components/ScratchPad';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import FocusMode from '@/components/focus/FocusMode';
import AskAITutorButton from '@/components/focus/AskAITutorButton';
import FocusCelebration from '@/components/focus/FocusCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timer duration in seconds (25 questions = 25 minutes for mock exams)
const MOCK_EXAM_TIME = 25 * 60; // 25 minutes in seconds

// Stages that use "Revision Set" instead of "Mock"
const REVISION_STAGES = ['gcse', 'a-levels', 'year-9', 'year-10'];

const PracticePaper = ({ progress }) => {
  const navigate = useNavigate();
  const { stage, subject, paperType, paperNumber } = useParams();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [examCompleted, setExamCompleted] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [wasForceStop, setWasForceStop] = useState(false);
  
  // Timer states
  const [timeRemaining, setTimeRemaining] = useState(MOCK_EXAM_TIME);
  const [timerActive, setTimerActive] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const timerRef = useRef(null);
  
  // Scratch pad state
  const [scratchPadOpen, setScratchPadOpen] = useState(false);
  
  // Track if practice paper has been saved (to avoid duplicate saves)
  const [practiceSaved, setPracticeSaved] = useState(false);

  // One-question-per-screen index for Focus Mode (practice/custom only).
  const [currentIdx, setCurrentIdx] = useState(0);
  
  // Check if this stage uses "Revision" label instead of "Mock"
  const isRevisionStage = REVISION_STAGES.includes(stage);
  const mockLabel = isRevisionStage ? 'Revision Set' : 'Mock Exam';

  useEffect(() => {
    loadPaper();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [stage, subject, paperType, paperNumber]);

  // Timer effect
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive]);

  // Auto-save practice paper when all questions are answered
  useEffect(() => {
    const isPractice = paperType === 'practice';
    const allAnswered = questions.length > 0 && Object.keys(answers).length === questions.length;
    
    if (isPractice && allAnswered && !practiceSaved && !examCompleted) {
      // Save the practice attempt automatically when all questions are answered
      saveTestAttempt();
      setPracticeSaved(true);
    }
  }, [answers, questions.length, paperType, practiceSaved, examCompleted]);

  const loadPaper = async () => {
    try {
      const token = localStorage.getItem('token');
      let response;
      
      // Add cache-busting timestamp
      const cacheBuster = `?t=${Date.now()}`;
      
      // Check if this is a custom bank paper
      if (paperType === 'custom') {
        // paperNumber contains the bank ID for custom papers
        response = await axios.get(`${API}/custom-papers/${stage}/${subject}/${paperNumber}/paper/1${cacheBuster}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
      } else {
        response = await axios.get(`${API}/papers/${stage}/${subject}/${paperType}/${paperNumber}${cacheBuster}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
      }
      
      setQuestions(response.data.questions);
      setLoading(false);
    } catch (error) {
      console.error('Error loading paper:', error);
      if (error.response?.status === 403) {
        // Subscription access denied
        toast.error(error.response?.data?.detail || 'Upgrade required to access this content');
        navigate('/subscription');
      } else {
        toast.error('Failed to load paper');
      }
      setLoading(false);
    }
  };

  const startExam = () => {
    setExamStarted(true);
    if (paperType === 'mock') {
      setTimerActive(true);
    }
  };

  const handleTimeUp = () => {
    setTimerActive(false);
    setExamCompleted(true);
    toast.warning('Time is up! Your exam has been submitted.');
    saveTestAttempt();
  };

  const handleAnswerChange = (questionId, answer) => {
    // Don't allow changing if exam is already completed
    if (examCompleted) {
      return;
    }
    
    const answerValue = parseInt(answer);
    setAnswers(prev => ({ ...prev, [questionId]: answerValue }));
  };

  const toggleShowAnswer = (questionId) => {
    setShowAnswers(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const checkAnswer = (questionId) => {
    if (answers[questionId] === undefined) return null;
    const question = questions.find(q => q.id === questionId);
    return answers[questionId] === question.correct_answer;
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach(q => {
      if (answers[q.id] === q.correct_answer) correct++;
    });
    return {
      correct,
      total: questions.length,
      percentage: Math.round((correct / questions.length) * 100)
    };
  };

  const getWrongAnswers = () => {
    return questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== q.correct_answer);
  };

  const saveTestAttempt = async () => {
    try {
      const token = localStorage.getItem('token');
      const score = calculateScore();
      
      // Don't save test attempts for custom papers (they're not part of progression)
      if (paperType !== 'custom') {
        await axios.post(`${API}/student/save-attempt`, {
          stage,
          subject,
          test_type: paperType,
          paper_number: parseInt(paperNumber),
          score: score.percentage,
          correct_answers: score.correct,
          total_questions: score.total,
          time_taken: MOCK_EXAM_TIME - timeRemaining
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Record activity for streak (for all paper types including custom)
      await axios.post(`${API}/student/record-activity`, {
        activityType: 'test_completed',
        score: score.percentage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
    } catch (error) {
      console.error('Error saving test attempt:', error);
    }
  };

  const submitExam = () => {
    setTimerActive(false);
    setExamCompleted(true);
    saveTestAttempt();
    toast.success('Exam submitted successfully!');
  };

  const handleStop = () => {
    setShowStopDialog(true);
  };

  const confirmStop = () => {
    setShowStopDialog(false);
    setTimerActive(false);
    setExamCompleted(true);
    setWasForceStop(true);
    saveTestAttempt();
    toast.info(`Exam stopped. ${Object.keys(answers).length} questions answered.`);
  };

  const handleRetake = () => {
    // Reset all state
    setAnswers({});
    setShowAnswers({});
    setTimeRemaining(MOCK_EXAM_TIME);
    setExamCompleted(false);
    setShowReview(false);
    setWasForceStop(false);
    setExamStarted(false);
    setTimerActive(false);
    setPracticeSaved(false);  // Reset saved state so new attempt can be saved
    setCurrentIdx(0);
    loadPaper();
  };

  const handleClose = () => {
    navigate(-1);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading paper...</p>
        </div>
      </div>
    );
  }

  const isPractice = paperType === 'practice';
  const isMock = paperType === 'mock';
  const isCustom = paperType === 'custom';
  const progressPercent = (Object.keys(answers).length / questions.length) * 100;
  const score = calculateScore();
  const allAnswered = Object.keys(answers).length === questions.length;
  const wrongAnswers = getWrongAnswers();

  // Get paper title
  const getPaperTitle = () => {
    if (isCustom) {
      return 'Custom Practice';
    }
    return isMock ? mockLabel : 'Practice Paper';
  };

  // Start exam screen for mock exams
  if (isMock && !examStarted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card className="max-w-lg w-full shadow-2xl" data-testid="start-exam-card">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg text-center">
            <CardTitle className="text-3xl font-bold">{mockLabel}</CardTitle>
            <p className="text-white/90 mt-2">Paper {paperNumber} - {subject.replace('-', ' ')}</p>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="text-center space-y-4">
              <div className="bg-purple-100 p-6 rounded-xl">
                <Clock className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                <p className="text-2xl font-bold text-purple-900">25 Minutes</p>
                <p className="text-purple-700">Time Limit</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-gray-900">{questions.length}</p>
                  <p className="text-gray-600">Questions</p>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-gray-900">1 min</p>
                  <p className="text-gray-600">Per Question</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900">Important Instructions:</p>
                    <ul className="text-sm text-amber-800 mt-2 space-y-1">
                      <li>• You can change your answers before submitting</li>
                      <li>• The timer will start when you click "Start Exam"</li>
                      <li>• Exam auto-submits when time runs out</li>
                      <li>• Results shown only after submission</li>
                      <li>• Review wrong answers after completion</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate(-1)} className="flex-1">
                <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
              </Button>
              <Button onClick={startExam} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="start-exam-btn">
                Start Exam
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Review screen for wrong answers
  if (showReview && wrongAnswers.length > 0) {
    return (
      <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl" data-testid="review-card">
            <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold">Review Wrong Answers</CardTitle>
                  <p className="text-white/90 mt-1">{wrongAnswers.length} questions to review</p>
                </div>
                <Button variant="ghost" onClick={() => setShowReview(false)} className="text-white hover:bg-white/20">
                  Back to Results
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {wrongAnswers.map((q, index) => (
                <Card key={q.id} className="border-2 border-red-200 bg-red-50" data-testid={`review-question-${q.id}`}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary">{q.topic}</Badge>
                          <Badge variant="outline">{q.difficulty}</Badge>
                        </div>
                        <CardTitle className="text-lg">{q.question}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {q.options.map((option, idx) => {
                        const isSelected = answers[q.id] === idx;
                        const isCorrect = idx === q.correct_answer;
                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg ${
                              isCorrect 
                                ? 'bg-green-100 border-2 border-green-400' 
                                : isSelected 
                                ? 'bg-red-100 border-2 border-red-400' 
                                : 'bg-white border'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {isCorrect && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                              {isSelected && !isCorrect && <XCircle className="h-5 w-5 text-red-600" />}
                              <span className={isCorrect ? 'font-semibold text-green-800' : isSelected ? 'text-red-800' : ''}>
                                {option}
                              </span>
                              {isCorrect && <span className="text-green-600 text-sm ml-auto">Correct Answer</span>}
                              {isSelected && !isCorrect && <span className="text-red-600 text-sm ml-auto">Your Answer</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Explanation:
                      </p>
                      <p className="text-blue-800">{q.explanation}</p>
                      
                      {q.step_by_step && q.step_by_step.length > 0 && (
                        <div className="mt-4">
                          <p className="font-semibold text-blue-900 mb-2">Step-by-Step:</p>
                          <div className="space-y-1">
                            {q.step_by_step.map((step, stepIdx) => (
                              <p key={stepIdx} className="text-blue-800 text-sm">{step}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => setShowReview(false)}>
                  Back to Results
                </Button>
                <Button onClick={() => navigate(-1)} className="bg-purple-600 hover:bg-purple-700">
                  Back to Papers
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Practice / Custom paper → Seneca-style Focus Mode (one question per screen).
  // Mock exams keep the existing all-questions-on-page layout (per user request).
  if ((isPractice || isCustom || (isMock && examStarted)) && !examCompleted && !showReview && questions.length > 0) {
    const safeIdx = Math.min(currentIdx, questions.length - 1);
    const q = questions[safeIdx];
    const hasAnswered = answers[q.id] !== undefined;
    // Mock exam mode keeps everything blind until submission — no immediate
    // feedback colours, no solution reveal, no per-question explanation.
    const showCorrectness = hasAnswered && !isMock;
    const showSolution = !isMock && !!showAnswers[q.id];
    const subjectLabel = subject.replace(/-/g, ' ');
    const titleLabel = isCustom
      ? `Custom Practice · ${subjectLabel}`
      : isMock
        ? `Mock Exam ${paperNumber} · ${subjectLabel}`
        : `Practice Paper ${paperNumber} · ${subjectLabel}`;

    return (
      <FocusMode
        title={titleLabel}
        current={safeIdx + 1}
        total={questions.length}
        timerMode={isMock ? 'countdown' : null}
        timerSeconds={isMock ? timeRemaining : null}
        onTimeUp={isMock ? handleTimeUp : undefined}
        onPrev={() => setCurrentIdx((i) => Math.max(i - 1, 0))}
        onNext={() => {
          if (safeIdx < questions.length - 1) {
            setCurrentIdx((i) => i + 1);
          } else if (allAnswered) {
            // last question + all answered → wrap to results
            setExamCompleted(true);
            if (isMock) {
              setTimerActive(false);
              saveTestAttempt();
            } else if (!practiceSaved) {
              saveTestAttempt();
              setPracticeSaved(true);
            }
          }
        }}
        canPrev={safeIdx > 0}
        canNext={safeIdx < questions.length - 1 || allAnswered}
        onExit={() => navigate(-1)}
        showEstimate
        tutorSlot={
          <AskAITutorButton
            endpoint="/api/ai-tutor/chat"
            context={{
              subject: subjectLabel,
              topic: q.topic,
              question: q.question,
              options: q.options,
              stage,
            }}
          />
        }
      >
        <FocusCelebration correctCount={score.correct} />
        {/* Topic + difficulty pills */}
        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
          {q.topic && (
            <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-700 dark:text-purple-300">{q.topic}</span>
          )}
          {q.difficulty && (
            <span className="px-2 py-0.5 rounded-full bg-current/10 capitalize">{q.difficulty}</span>
          )}
        </div>

        <h2 className="leading-snug font-semibold mb-6" data-testid="focus-question-text">
          {q.question}
        </h2>

        <RadioGroup
          name={`question-${q.id}`}
          value={answers[q.id] !== undefined ? answers[q.id].toString() : ''}
          onValueChange={(val) => handleAnswerChange(q.id, val)}
          className="space-y-3"
        >
          {q.options.map((option, idx) => {
            const isSelected = answers[q.id] === idx;
            const isCorrectOption = idx === q.correct_answer;
            // showCorrectness is true only for non-mock practice — mock keeps
            // colours neutral so the student doesn't get tipped off mid-exam.
            return (
              <label
                key={idx}
                htmlFor={`focus-${q.id}-${idx}`}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-2 focus-within:ring-offset-transparent ${
                  showCorrectness
                    ? isCorrectOption
                      ? 'bg-emerald-500/10 border-emerald-500'
                      : isSelected
                        ? 'bg-rose-500/10 border-rose-500'
                        : 'border-current/20 opacity-70'
                    : isSelected
                      ? 'bg-purple-500/10 border-purple-500'
                      : 'border-current/20 hover:border-purple-500 hover:bg-purple-500/5'
                }`}
                data-testid={`focus-option-${idx}`}
              >
                <RadioGroupItem value={idx.toString()} id={`focus-${q.id}-${idx}`} />
                <span className="flex-1">{option}</span>
                {showCorrectness && isCorrectOption && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                {showCorrectness && isSelected && !isCorrectOption && <XCircle className="h-5 w-5 text-rose-600" />}
              </label>
            );
          })}
        </RadioGroup>

        {hasAnswered && !isMock && (
          <div className="mt-6 space-y-3">
            <Button
              onClick={() => toggleShowAnswer(q.id)}
              variant="outline"
              className="w-full border-current/30"
              data-testid="focus-show-solution-btn"
            >
              <Lightbulb className="mr-2 h-4 w-4" />
              {showSolution ? 'Hide' : 'Show'} Detailed Solution
            </Button>

            {showSolution && (
              <div
                className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/40"
                data-testid="focus-solution-block"
              >
                <p className="font-semibold mb-2 flex items-center gap-2 text-sky-700 dark:text-sky-300">
                  <BookOpen className="h-4 w-4" /> Explanation
                </p>
                <p className="text-sm opacity-90 leading-relaxed">{q.explanation}</p>
                {q.step_by_step && q.step_by_step.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold mb-2 text-sky-700 dark:text-sky-300">Step-by-step</p>
                    <ol className="space-y-1.5 list-decimal list-inside text-sm opacity-90">
                      {q.step_by_step.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Bottom strip — answered count + finish/submit CTA when all answered */}
        <div className="mt-6 pt-4 border-t border-current/10 flex items-center justify-between text-xs">
          <span className="opacity-70">
            {Object.keys(answers).length} of {questions.length} answered
          </span>
          {allAnswered && (
            <Button
              size="sm"
              onClick={() => {
                setExamCompleted(true);
                if (isMock) {
                  setTimerActive(false);
                  saveTestAttempt();
                } else if (!practiceSaved) {
                  saveTestAttempt();
                  setPracticeSaved(true);
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="focus-finish-btn"
            >
              <Trophy className="mr-1.5 h-3.5 w-3.5" />
              {isMock ? 'Submit Mock Exam' : 'Finish & See Results'}
            </Button>
          )}
        </div>
      </FocusMode>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Scratch Pad Component */}
      <ScratchPad isOpen={scratchPadOpen} onClose={() => setScratchPadOpen(false)} />
      
      {/* Floating Scratch Pad Button - positioned to avoid Emergent badge */}
      <Button
        onClick={() => setScratchPadOpen(true)}
        className="fixed bottom-24 right-6 z-30 rounded-full w-14 h-14 bg-purple-600 hover:bg-purple-700 shadow-lg"
        data-testid="open-scratch-pad"
        title="Open Scratch Pad"
      >
        <PenLine className="h-6 w-6" />
      </Button>
      
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-2xl animate-fade-in" data-testid="practice-paper">
          <CardHeader className={`bg-gradient-to-r ${isCustom ? 'from-amber-500 to-orange-500' : isPractice ? 'from-green-600 to-emerald-600' : 'from-purple-600 to-indigo-600'} text-white rounded-t-lg sticky top-0 z-10`}>
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/20"
                data-testid="back-button"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
              </Button>
              
              {/* Timer for Mock Exams */}
              {isMock && !examCompleted && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  timeRemaining <= 60 ? 'bg-red-500 animate-pulse' : 
                  timeRemaining <= 300 ? 'bg-amber-500' : 'bg-white/20'
                }`} data-testid="exam-timer">
                  <Clock className="h-5 w-5" />
                  <span className="text-xl font-bold font-mono">{formatTime(timeRemaining)}</span>
                </div>
              )}
              
              {/* Stop button for mock exams */}
              {isMock && !examCompleted && (
                <Button
                  onClick={handleStop}
                  variant="destructive"
                  className="bg-red-500 hover:bg-red-600"
                  data-testid="stop-exam-btn"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </Button>
              )}
              
              {!isMock && (
                <Badge className={`font-semibold ${isCustom ? 'bg-amber-100 text-amber-700' : 'bg-white text-purple-700'}`}>
                  {isCustom ? 'Custom Bank' : 'Practice Mode'}
                </Badge>
              )}
            </div>
            <CardTitle className="text-3xl font-bold">
              {isCustom ? 'Custom Practice' : isPractice ? 'Practice' : (isRevisionStage ? 'Revision' : 'Mock')} {!isCustom && `Paper ${paperNumber}`}
            </CardTitle>
            <p className="text-white/90 mt-2">{stage.toUpperCase()} - {subject.replace(/-/g, ' ')}</p>
            
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Progress</span>
                <span className="font-semibold">{Object.keys(answers).length} / {questions.length}</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-white/30" />
            </div>

            {/* Submit button for mock exams */}
            {isMock && !examCompleted && allAnswered && (
              <Button 
                onClick={submitExam} 
                className="mt-4 w-full bg-white text-purple-700 hover:bg-white/90 font-semibold"
                data-testid="submit-exam-btn"
              >
                Submit Exam
              </Button>
            )}

            {/* Score display when completed */}
            {examCompleted && (
              <div className="mt-4 bg-white/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Your Score</p>
                    <p className="text-3xl font-bold">{score.correct}/{score.total}</p>
                    {wasForceStop && (
                      <p className="text-xs text-white/70 mt-1">
                        {Object.keys(answers).length} questions answered (stopped early)
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold">{score.percentage}%</p>
                    <p className="text-sm">{score.percentage >= 70 ? 'Excellent!' : 'Keep practicing!'}</p>
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-3 mt-4">
                  {wrongAnswers.length > 0 && (
                    <Button 
                      onClick={() => setShowReview(true)} 
                      variant="outline" 
                      className="flex-1 bg-white/10 border-white/30 text-white hover:bg-white/20"
                      data-testid="review-wrong-btn"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Review {wrongAnswers.length} Wrong
                    </Button>
                  )}
                  <Button
                    onClick={handleRetake}
                    variant="outline"
                    className="flex-1 bg-white/10 border-white/30 text-white hover:bg-white/20"
                    data-testid="retake-paper-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retake
                  </Button>
                  <Button
                    onClick={handleClose}
                    className="flex-1 bg-white text-purple-700 hover:bg-white/90"
                    data-testid="close-paper-btn"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Close
                  </Button>
                </div>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {questions.map((q, index) => {
              const isCorrect = checkAnswer(q.id);
              const hasAnswered = answers[q.id] !== undefined;
              const showSolution = showAnswers[q.id];
              // Only show correctness for practice mode (immediately) or mock exams (after completion)
              const showCorrectness = hasAnswered && (isPractice || examCompleted);

              return (
                <Card key={q.id} className="border-2 transition-colors hover:border-purple-300" data-testid={`question-${q.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <Badge variant="secondary">{q.topic}</Badge>
                          <Badge variant="outline">{q.difficulty}</Badge>
                        </div>
                        <CardTitle className="text-lg font-semibold">{q.question}</CardTitle>
                      </div>
                      {showCorrectness && (
                        <div>
                          {isCorrect ? (
                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                          ) : (
                            <XCircle className="h-8 w-8 text-red-600" />
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <RadioGroup 
                      name={`question-${q.id}`}
                      value={answers[q.id] !== undefined ? answers[q.id].toString() : ""}
                      onValueChange={(val) => handleAnswerChange(q.id, val)}
                      disabled={examCompleted}
                    >
                      {q.options.map((option, idx) => {
                        const isSelected = answers[q.id] === idx;
                        const isCorrectOption = idx === q.correct_answer;

                        return (
                          <div
                            key={idx}
                            className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                              showCorrectness
                                ? isCorrectOption
                                  ? 'bg-green-50 border-2 border-green-300'
                                  : isSelected
                                  ? 'bg-red-50 border-2 border-red-300'
                                  : 'bg-gray-50'
                                : isSelected
                                ? 'bg-purple-50 border-2 border-purple-300'
                                : examCompleted
                                ? 'bg-gray-50'
                                : 'hover:bg-gray-50 cursor-pointer'
                            }`}
                            data-testid={`question-${q.id}-option-${idx}`}
                          >
                            <RadioGroupItem 
                              value={idx.toString()} 
                              id={`${q.id}-${idx}`} 
                              disabled={examCompleted}
                            />
                            <Label 
                              htmlFor={`${q.id}-${idx}`} 
                              className={`flex-1 ${examCompleted ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              {option}
                              {showCorrectness && isCorrectOption && (
                                <CheckCircle2 className="inline-block ml-2 h-4 w-4 text-green-600" />
                              )}
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>

                    {/* Show solution for practice mode or after exam completion */}
                    {(isPractice || examCompleted) && hasAnswered && (
                      <div className="space-y-3">
                        <Button
                          onClick={() => toggleShowAnswer(q.id)}
                          variant="outline"
                          className="w-full"
                          data-testid={`show-solution-${q.id}`}
                        >
                          <Lightbulb className="mr-2 h-4 w-4" />
                          {showSolution ? 'Hide' : 'Show'} Detailed Solution
                        </Button>

                        {showSolution && (
                          <Card className="bg-blue-50 border-blue-200 animate-fade-in">
                            <CardContent className="p-4 space-y-4">
                              <div>
                                <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                  <BookOpen className="h-4 w-4" />
                                  Explanation:
                                </p>
                                <p className="text-blue-800">{q.explanation}</p>
                              </div>

                              {q.step_by_step && q.step_by_step.length > 0 && (
                                <div>
                                  <p className="font-semibold text-blue-900 mb-3">Step-by-Step Solution:</p>
                                  <div className="space-y-2">
                                    {q.step_by_step.map((step, stepIdx) => (
                                      <div key={stepIdx} className="flex items-start gap-3">
                                        <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                          {stepIdx + 1}
                                        </div>
                                        <p className="text-blue-800 flex-1">{step}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Final results card */}
            {examCompleted && (
              <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300">
                <CardContent className="p-6 text-center">
                  <Trophy className="h-16 w-16 text-purple-600 mx-auto mb-4" />
                  <p className="text-2xl font-bold text-purple-900 mb-2">
                    {wasForceStop ? '📋 Exam Stopped' : score.percentage >= 70 ? '🎉 Great Job!' : '💪 Keep Practicing!'}
                  </p>
                  <p className="text-purple-700 mb-2">
                    You scored {score.correct} out of {score.total} ({score.percentage}%)
                  </p>
                  {wasForceStop && (
                    <p className="text-sm text-purple-500 mb-4">
                      {Object.keys(answers).length} of {questions.length} questions answered
                    </p>
                  )}
                  <div className="flex gap-4 justify-center flex-wrap">
                    {wrongAnswers.length > 0 && (
                      <Button onClick={() => setShowReview(true)} variant="outline" className="border-purple-300">
                        <XCircle className="mr-2 h-4 w-4" />
                        Review Wrong Answers
                      </Button>
                    )}
                    <Button 
                      onClick={handleRetake} 
                      variant="outline"
                      className="border-purple-300"
                      data-testid="retake-final-btn"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Retake
                    </Button>
                    <Button 
                      onClick={handleClose} 
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="close-final-btn"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Close
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Practice mode - show score when all answered */}
            {isPractice && allAnswered && !examCompleted && (
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
                <CardContent className="p-6 text-center">
                  <p className="text-2xl font-bold text-green-900 mb-2">
                    {score.percentage >= 70 ? '🎉 Great Job!' : '💪 Keep Practicing!'}
                  </p>
                  <p className="text-green-700 mb-4">
                    You scored {score.correct} out of {score.total} ({score.percentage}%)
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button 
                      onClick={handleRetake} 
                      variant="outline"
                      className="border-green-300"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Retake
                    </Button>
                    <Button 
                      onClick={handleClose} 
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Close
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Stop Exam Confirmation Dialog */}
        <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
          <AlertDialogContent data-testid="stop-confirmation-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600">Stop Exam?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to stop this exam? You have answered {Object.keys(answers).length} of {questions.length} questions. 
                Your current answers will be submitted and scored.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-stop">Continue Exam</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmStop} 
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-stop"
              >
                Yes, Stop & Submit
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default PracticePaper;
